import {
  type TableFilter,
  type ValueOf,
  unreadonly,
  validation,
} from '@ipfabric/utils';
import { invertRecord } from '@ipfabric/utils/utils/invertRecord';
import { record } from 'fp-ts';

export const getDefaultOperator = (
  filter: TableFilter,
  // string value entered into simple filter
  filterValue: string,
): string => {
  switch (filter.type) {
    case 'routing':
    case 'routingIpv6':
    case 'text':
      return 'like';
    case 'ip':
      return validation.cidr.validate(filterValue).error ? 'like' : 'cidr';
    case 'ipv6':
      return validation.cidrIpv6.validate(filterValue).error ? 'like' : 'cidr';
    default:
      return 'eq';
  }
};

/**
 * Defines subset of API operators that can be used in simple filter
 * and their human-readable form.
 *
 * Actual set of operators that appears on column is subset of these
 * entries supported by column's filter.
 */
export const operatorReadableMap = {
  gte: '>=',
  gt: '>',
  lte: '<=',
  lt: '<',
  reg: '=~',
  nreg: '!=~',
  eq: '=',
  neq: '!=',
  ip: 'IP:',
  sect: '@',
  nsect: '!@',
} as const;
const readableOperatorMap = invertRecord(operatorReadableMap);

export type AllowedOperator = keyof typeof operatorReadableMap;
export type AllowedOperatorReadable = ValueOf<typeof operatorReadableMap> | '';

/**
 * Convert operator to human-readable form
 *
 * Empty string is returned when operator is not supported because usages rely on it
 * from historical reasons; may be improved in the future with filters refactoring.
 */
export const getReadableFromOperator = (
  operator: string,
): AllowedOperatorReadable | '' =>
  operatorReadableMap[operator as AllowedOperator] ?? '';

export const getOperatorFromReadable = (
  operator: AllowedOperatorReadable,
): AllowedOperator | undefined =>
  operator === '' ? undefined : readableOperatorMap[operator];

export const selectSimpleFilterOperators = unreadonly(['eq', 'neq'] as const);
export type SelectSimpleFilterOperator =
  (typeof selectSimpleFilterOperators)[number];

export const textSimpleFilterOperators = unreadonly([
  'reg',
  'nreg',
  'eq',
  'neq',
] as const);
export type TextSimpleFilterOperator =
  (typeof textSimpleFilterOperators)[number];

export const getAllowedOperators = (filter: TableFilter): AllowedOperator[] =>
  record
    .keys(operatorReadableMap)
    .filter((operator) => filter.operators.includes(operator));
